import { Student, Task } from '@/types';
import { photoService } from '@/services/photoService';
import { mockTasks } from './mockTasks';
import { mockInterventionTasks } from './mockInterventionTasks';

// Split names by gender
const maleFirstNames = [
  'Liam', 'Noah', 'Oliver', 'James', 'Elijah', 'William', 'Henry', 'Lucas', 'Benjamin', 'Theodore',
  'Mateo', 'Jack', 'Daniel', 'Alexander', 'Owen', 'Wei', 'Jin', 'Ming', 'Raj', 'Arjun', 'Arun',
  'Miguel', 'Diego', 'Omar', 'Hassan'
];

const femaleFirstNames = [
  'Sofia', 'Charlotte', 'Ava', 'Amelia', 'Isabella', 'Mia', 'Luna', 'Harper', 'Gianna', 'Evelyn',
  'Hui', 'Yong', 'Aisha', 'Fatima', 'Zainab', 'Priya', 'Deepa', 'Isabella', 'Valentina'
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
  'Chen', 'Wong', 'Liu', 'Zhang', 'Li', 'Patel', 'Singh', 'Kumar', 'Shah', 'Sharma',
  'Kim', 'Lee', 'Park', 'Choi', 'Jung', 'Ali', 'Ahmed', 'Hassan', 'Khan', 'Mohammad',
  'Nguyen', 'Tran', 'Pham', 'Le', 'Vu', 'Sato', 'Suzuki', 'Takahashi', 'Tanaka', 'Watanabe'
];

// Helper functions for generating random data
const randomDate = (start: Date, end: Date) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

const randomPhone = () => {
  return `(${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
};

const randomGPA = (min: number = 2.0, max: number = 4.0) => {
  return Number((Math.random() * (max - min) + min).toFixed(2));
};

const randomAttendance = (min: number = 85, max: number = 100) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Generate class schedule
const subjects = [
  { name: 'Mathematics', teachers: ['Mr. Thompson', 'Ms. Garcia', 'Dr. Chen'] },
  { name: 'English Literature', teachers: ['Mrs. Anderson', 'Mr. Williams', 'Ms. Taylor'] },
  { name: 'Biology', teachers: ['Dr. Martinez', 'Ms. Johnson', 'Mr. Lee'] },
  { name: 'Chemistry', teachers: ['Dr. Wilson', 'Mrs. Brown', 'Mr. Davis'] },
  { name: 'Physics', teachers: ['Dr. Smith', 'Mr. Rodriguez', 'Ms. White'] },
  { name: 'World History', teachers: ['Mrs. Miller', 'Mr. Jones', 'Dr. Clark'] },
  { name: 'Physical Education', teachers: ['Coach Johnson', 'Coach Martinez', 'Coach Wilson'] },
  { name: 'Art', teachers: ['Ms. Adams', 'Mrs. Turner', 'Mr. Ross'] },
  { name: 'Music', teachers: ['Mr. Young', 'Ms. King', 'Mrs. Scott'] },
  { name: 'Computer Science', teachers: ['Mr. Park', 'Ms. Lee', 'Dr. Kumar'] }
];

const generateSchedule = (grade: number) => {
  const shuffledSubjects = [...subjects].sort(() => Math.random() - 0.5);
  const periodsPerDay = 7;
  const schedule = [];

  for (let period = 1; period <= periodsPerDay; period++) {
    const subject = shuffledSubjects[(period - 1) % shuffledSubjects.length];
    const teacher = subject.teachers[Math.floor(Math.random() * subject.teachers.length)];
    const floor = Math.floor((grade - 9) / 2) + 1;
    
    schedule.push({
      period,
      className: subject.name,
      classroomNumber: `${floor}${String(period).padStart(2, '0')}`,
      teacherName: teacher
    });
  }

  return schedule;
};

// Assign tasks to students based on their academic performance and attendance
const assignTasks = (studentId: string, gpa: number, attendanceRate: number): Task[] => {
  const allTasks = [...mockTasks, ...mockInterventionTasks];
  const numTasks = Math.floor(Math.random() * 7) + 4; // 4-10 tasks
  const assignedTasks = [];

  // Create a pool of tasks based on student needs
  const taskPool = [];

  // Academic tasks
  if (gpa < 2.5) {
    taskPool.push(...allTasks.filter(task => task.category === 'academic'));
  }

  // Attendance tasks
  if (attendanceRate < 90) {
    taskPool.push(...allTasks.filter(task => task.category === 'attendance'));
  }

  // Behavioral and health tasks
  taskPool.push(...allTasks.filter(task => 
    task.category === 'behavioral' || task.category === 'health'
  ));

  // Randomly select tasks from the pool
  while (assignedTasks.length < numTasks && taskPool.length > 0) {
    const randomIndex = Math.floor(Math.random() * taskPool.length);
    const task = taskPool[randomIndex];
    
    // Create a new task instance with unique ID and assigned to the student
    assignedTasks.push({
      ...task,
      id: `${task.id}-${studentId}`,
      assignedTo: studentId,
      status: ['not_started', 'in_process', 'completed'][Math.floor(Math.random() * 3)],
      dueDate: new Date(Date.now() + (Math.random() * 30 + 1) * 24 * 60 * 60 * 1000).toISOString()
    } as Task);
    
    // Remove used task from pool
    taskPool.splice(randomIndex, 1);
  }

  return assignedTasks;
};

export const mockStudentData: Student = {
  id: '12345',
  firstName: 'John',
  lastName: 'Doe',
  dateOfBirth: '2005-01-01',
  gender: 'male',
  grade: 10,
  enrollmentDate: '2020-08-15',
  schedule: [
      {
          period: 1,
          className: 'Math',
          classroomNumber: '101',
          teacherName: 'Mr. Smith',
      },
      // ... other schedule entries ...
  ],
  address: '123 Main St',
  city: 'Anytown',
  state: 'CA',
  zipCode: '12345',
  primaryPhone: '555-1234',
  email: 'john.doe@example.com',
  guardians: [], // Add guardian data here
  academicPerformance: {
      gpa: 3.5,
      credits: 20,
      coursesFailing: 0,
  },
  academicAdvisor: 'Mrs. Johnson',
  homeroomTeacher: 'Mr. Smith',
  attendanceRate: 95,
  attendanceStatus: 'active',
  lastAttendanceDate: '2023-10-01',
  daysAbsent: 2,
  daysPresent: 28,
  tardinessCount: 1,
  attendanceRecords: [], // Add attendance records here
  interventions: [], // Add intervention data here
  riskFactors: [], // Add risk factors here
  transportation: 'Bus',
  fosterCare: false,
  homelessStatus: false,
  lunchEligibility: true,
  secondaryLanguage: 'Spanish',
  primaryLanguage: 'English',
  ethnicity: 'Hispanic',
  supportServices: 'None',
  communityReferrals: 'None',
  barriers: 'None',
  supportPlanStatus: 'Not Started',
  extracurricular: 'Soccer',
  behavioralNotes: 'N/A',
  counselingSessions: 0,
  behavioralIncidents: 0,
  emergencyContactPhone: '555-5678',
  emergencyContactName: 'Jane Doe',
  immunizationStatus: 'Complete',
  medications: 'None',
  medicalConditions: 'None',
  allergies: 'None',
  iepStatus: 'No',
  previousPerformance: 'Above Average',
  secondaryPhone: '555-8765',
  preferredName: 'Johnny',
  interests: 'Reading, Sports',
  digitalAccess: 'Yes',
  tasks: [], // Add task data here
  primaryGuardianName: 'Jane Doe',
  primaryGuardianRelationship: 'Mother',
  primaryGuardianPhone: '555-5678',
  primaryGuardianEmail: 'jane.doe@example.com',
};

// Generate a single student record
const generateStudent = (index: number): Student => {
  const gender = Math.random() > 0.5 ? 'male' : 'female';
  const firstName = gender === 'male' 
    ? maleFirstNames[Math.floor(Math.random() * maleFirstNames.length)]
    : femaleFirstNames[Math.floor(Math.random() * femaleFirstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const grade = Math.floor(Math.random() * 4) + 9; // Grades 9-12
  const gpa = randomGPA();
  const attendanceRate = randomAttendance();
  const studentId = `ST${String(index + 1).padStart(4, '0')}`;

  const student: Student = {
    ...mockStudentData,
    id: studentId,
    firstName,
    lastName,
    middleName: Math.random() > 0.7 ? 
      (gender === 'male' ? maleFirstNames[Math.floor(Math.random() * maleFirstNames.length)] : 
                          femaleFirstNames[Math.floor(Math.random() * femaleFirstNames.length)]) : 
      undefined,
    dateOfBirth: randomDate(new Date(2005, 0, 1), new Date(2008, 11, 31)).toISOString().split('T')[0],
    gender,
    grade,
    enrollmentDate: '2023-08-15',
    schedule: generateSchedule(grade),
    address: `${Math.floor(Math.random() * 9999) + 100} Main Street`,
    city: 'Springfield',
    state: 'IL',
    zipCode: '62701',
    primaryPhone: randomPhone(),
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@student.edu`,
    guardians: [],
    academicPerformance: {
      gpa,
      credits: (grade - 9) * 30 + Math.floor(Math.random() * 15),
      coursesFailing: gpa < 2.0 ? Math.floor(Math.random() * 3) + 1 : 0
    },
    academicAdvisor: `Dr. ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
    homeroomTeacher: `Ms. ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
    attendanceRate,
    attendanceStatus: attendanceRate < 85 ? 'chronic-absence' : 'active',
    lastAttendanceDate: '2023-11-15',
    daysAbsent: Math.floor((100 - attendanceRate) * 0.5),
    daysPresent: Math.floor(attendanceRate * 0.5),
    tardinessCount: Math.floor(Math.random() * 10),
    attendanceRecords: [],
    interventions: [],
    riskFactors: [],
    tasks: assignTasks(studentId, gpa, attendanceRate),
    transportation: 'Other',
    fosterCare: false,
    homelessStatus: false,
    lunchEligibility: false,
    secondaryLanguage: '',
    primaryLanguage: '',
    ethnicity: '',
    supportServices: '',
    communityReferrals: '',
    barriers: '',
    supportPlanStatus: 'Not Started',
    extracurricular: '',
    behavioralNotes: '',
    counselingSessions: 0,
    behavioralIncidents: 0,
    emergencyContactPhone: '',
    emergencyContactName: '',
    immunizationStatus: 'Complete',
    medications: '',
    medicalConditions: '',
    allergies: '',
    iepStatus: 'Yes',
    previousPerformance:'Average',
    secondaryPhone: '',
    preferredName: '',
    interests: '',
  };

  // Get photo from photo service
  student.photo = photoService.getStudentPhoto(student);

  return student;
};

// Generate 100 student records
export const mockStudents: Student[] = Array.from({ length: 100 }, (_, i) => generateStudent(i));
console.log('Students Generated', mockStudents);
